// ShortsDetail.js
'use strict';
'use client';
import { connect } from 'react-redux';
import { LRUCache } from 'lru-cache';

import { MERGE_OPERATION_DATA } from '../ActionTypes.js';

import {
  SHORTS_FEED_ALIAS,
  LIVESTREAM_CLIP_FEED_ALIAS,
  SHORTS_PREFETCHED_MESSAGE_AMOUNT,
  MONGO_OBJECT_ID_REGEX_PATTERN,
} from '../RemoteConfigKeys.js';

import getVodPlayerPoolSizeMaxKey from '../resource/getVodPlayerPoolSizeMaxKey.js';

import { replace } from '../action/navigationAction.js';
import fetchFeeds, { getReturnType } from '../action/fetchFeeds.js';
import setFirstShortMessagePlayed from '../action/setFirstShortMessagePlayed.js';
import addMessageIdToShortWatchedList from '../action/addMessageIdToShortWatchedList.js';

import ShortsDetail from '../page/ShortsDetail.jsx';
import withRouter from '../component/WithRouter.jsx';

import getMeData from '../selector/getMeData.js';
import getListData from '../selector/getListData.js';
import getOperationData from '../selector/getOperationData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getNetworkingSummary from '../selector/getNetworkingSummary.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getIsRemoteConfigMerged from '../selector/getIsRemoteConfigMerged.js';
import getShortsIds from '../selector/getShortsIds.js';

import {
  CONFIG_PRIORITY_PRESENCE_CLIENT,
  CONFIG_PRIORITY_PRESENCE_USER,
} from '../resource/configPriority.js';
import { breakpoint } from '../style/variables.js';

const cache = new LRUCache({ max: 256 });
const getSelectPath = ({ category } = {}) => {
  const key = `${category}`;
  let result = cache.get(key);
  if (!result) {
    result = ['shorts', category];
    cache.set(key, result);
  }
  return result;
};

const mapStateToProps = (state, { match, location }) => {
  const messageIdFromUrl = match?.params?.messageId;
  const ctaIdFromUrl = match?.params?.cta;
  const listPath = location?.state?.listPath;
  const prefetchedMessageAmount = getRemoteConfigData(
    state,
    SHORTS_PREFETCHED_MESSAGE_AMOUNT
  );
  const category =
    location?.state?.category || getRemoteConfigData(state, SHORTS_FEED_ALIAS);
  const clipCategory = getRemoteConfigData(state, LIVESTREAM_CLIP_FEED_ALIAS);
  const shortsSelectPath = listPath || getSelectPath({ category });
  const itemIds = listPath
    ? getListData(state, listPath, 'itemIds')
    : getShortsIds(state, category, clipCategory);
  const clipSelectPath = [
    'home',
    getReturnType({ feedName: clipCategory }),
    clipCategory,
  ];
  const nextPage = getListData(state, shortsSelectPath, 'nextPage');
  const clipNextPage = getListData(state, clipSelectPath, 'nextPage');
  const feedId = getOperationData(
    state,
    ['fetchedRenewFeedId', category],
    'id'
  );
  const deviceWidth = getOperationData(state, ['device'], 'width');
  const hasToken = !!getMeData(state, 'token');
  const isPresenceClientRemoteConfigMerged = getIsRemoteConfigMerged(
    state,
    CONFIG_PRIORITY_PRESENCE_CLIENT
  );
  const isPresenceUserRemoteConfigMerged = getIsRemoteConfigMerged(
    state,
    CONFIG_PRIORITY_PRESENCE_USER
  );

  return {
    deviceHeight: getOperationData(state, ['device'], 'height'),
    messageIdFromUrl,
    // Only shorts opened by a message id can be forbidden (ex: hidden message),
    // ad route has no message id to check.
    fetchMessageDetailError: messageIdFromUrl
      ? getNetworkingSummary(state, ['messages', messageIdFromUrl, 'detail'])
          ?.error
      : null,
    ctaIdFromUrl,
    feedId,
    clipFetchedFeedId: getOperationData(
      state,
      ['fetchedRenewFeedId', clipCategory],
      'id'
    ),
    clipLatestFeedId: getOperationData(
      state,
      ['renewFeedId', clipCategory],
      'id'
    ),
    category,
    clipCategory,
    shortsSelectPath,
    itemIds,
    nextPage,
    clipNextPage,
    clipLatestTimestamp: getOperationData(
      state,
      ['renewFeedId', clipCategory],
      'timestamp'
    ),
    isClipFirstPageFetching: !!getNetworkingData(
      state,
      [...clipSelectPath, 1],
      'isFetching'
    ),
    isClipFirstPageFetched: !!getNetworkingData(
      state,
      [...clipSelectPath, 1],
      'isFetched'
    ),
    isNextPageFetching: !!getNetworkingData(
      state,
      [...shortsSelectPath, nextPage],
      'isFetching'
    ),
    isFirstPageFetched: !!getNetworkingData(
      state,
      [...shortsSelectPath, 1],
      'isFetched'
    ),
    isFirstPageFetching: !!getNetworkingData(
      state,
      [...shortsSelectPath, 1],
      'isFetching'
    ),
    isOnDesktop: deviceWidth && deviceWidth > breakpoint.tablet,
    isConfigMerged: hasToken
      ? isPresenceUserRemoteConfigMerged
      : isPresenceClientRemoteConfigMerged,
    prefetchedMessageAmount,
    // Keyed off the device the same way playerPool sizes itself. Reading the
    // viewport here instead put a tablet on the desktop window (5 mounted
    // shorts) while the pool held the mobile max (3), leaving two mounted
    // shorts unable to ever acquire a player.
    playerPoolSize: getRemoteConfigData(state, getVodPlayerPoolSizeMaxKey()),
    mongoObjectIdRegexPattern: getRemoteConfigData(
      state,
      MONGO_OBJECT_ID_REGEX_PATTERN
    ),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchFeeds: payload => dispatch(fetchFeeds(payload)),
    setLastWatchedIndex: ({ lastWatchedIndex, feedId }) =>
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['feed', feedId],
          data: {
            lastWatchedIndex,
          },
        },
      }),
    replace: (...args) => dispatch(replace(...args)),
    setFirstShortMessagePlayed: data =>
      dispatch(setFirstShortMessagePlayed(data)),
    addMessageIdToShortWatchedList: data =>
      dispatch(addMessageIdToShortWatchedList(data)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ShortsDetail)
);
